import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Input, Modal, ModalSize } from '@column/column-ui-kit';

import { PopoverFilterTransferDateData } from '~/components';
import { DateInput } from '~/components/DateInput';
import { Flex } from '~/styles';
import { parseDateTime } from '~/util/dateFilterHelpers';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 var(--page-margin) 24px;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};

  &:first-child {
    padding-top: 8px;
  }
`;

const Label = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

interface FormValues {
  accountNumber: string;
  routingNumber: string;
  query: string;
  uiStartDate: { date?: string };
  uiEndDate: { date?: string };
}

interface CounterpartyFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialAccountNumber: string;
  initialRoutingNumber: string;
  initialQuery: string;
  initialDate: Partial<PopoverFilterTransferDateData>;
  convertDateRange: (params: {
    startDateString?: string;
    endDateString?: string;
  }) => Partial<PopoverFilterTransferDateData>;
  onApply: (values: {
    accountNumber: string;
    routingNumber: string;
    query: string;
    date: Partial<PopoverFilterTransferDateData>;
  }) => void;
}

export const CounterpartyFiltersModal: React.FC<CounterpartyFiltersModalProps> = ({
  isOpen,
  onClose,
  initialAccountNumber,
  initialRoutingNumber,
  initialQuery,
  initialDate,
  convertDateRange,
  onApply,
}) => {
  const methods = useForm<FormValues>({
    defaultValues: {
      accountNumber: initialAccountNumber,
      routingNumber: initialRoutingNumber,
      query: initialQuery,
      uiStartDate: initialDate.createdAtGte ? parseDateTime(initialDate.createdAtGte) : {},
      uiEndDate: initialDate.createdAtLt ? parseDateTime(initialDate.createdAtLt, true) : {},
    },
  });
  const { watch, setValue, reset, getValues } = methods;

  useEffect(() => {
    if (!isOpen) return;
    reset({
      accountNumber: initialAccountNumber,
      routingNumber: initialRoutingNumber,
      query: initialQuery,
      uiStartDate: initialDate.createdAtGte ? parseDateTime(initialDate.createdAtGte) : {},
      uiEndDate: initialDate.createdAtLt ? parseDateTime(initialDate.createdAtLt, true) : {},
    });
  }, [isOpen, initialAccountNumber, initialRoutingNumber, initialQuery, initialDate, reset]);

  const handleApply = () => {
    const fv = getValues();
    const datePayload = convertDateRange({
      startDateString: fv.uiStartDate?.date,
      endDateString: fv.uiEndDate?.date,
    });
    onApply({
      accountNumber: fv.accountNumber.trim(),
      routingNumber: fv.routingNumber.trim(),
      query: fv.query,
      date: {
        createdAtGte: datePayload.createdAtGte,
        createdAtLt: datePayload.createdAtLt,
      },
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Medium}>
      <Modal.Header title="Filters" />
      <FormProvider {...methods}>
        <Flex flexDirection="column" gap={16}>
          <Section>
            <Label>Routing Number</Label>
            <Input
              size="small"
              value={watch('routingNumber')}
              placeholder="Routing number"
              onChange={(value: string) => setValue('routingNumber', value)}
            />
          </Section>

          <Section>
            <Label>Account Number</Label>
            <Input
              size="small"
              value={watch('accountNumber')}
              placeholder="Account number"
              onChange={(value: string) => setValue('accountNumber', value)}
            />
          </Section>

          <Section>
            <Label>Search</Label>
            <Input
              size="small"
              value={watch('query')}
              placeholder="Search counterparties..."
              onChange={(value: string) => setValue('query', value)}
            />
          </Section>

          <Section>
            <Label>Created</Label>
            <Flex gap={12}>
              <Flex flexDirection="column" gap={8} auto>
                <Label>Start</Label>
                <DateInput
                  size="small"
                  value={watch('uiStartDate')?.date}
                  onChange={(value) => setValue('uiStartDate', { ...(watch('uiStartDate') ?? {}), date: value })}
                  renderInside
                />
              </Flex>
              <Flex flexDirection="column" gap={8} auto>
                <Label>End</Label>
                <DateInput
                  size="small"
                  value={watch('uiEndDate')?.date}
                  onChange={(value) => setValue('uiEndDate', { ...(watch('uiEndDate') ?? {}), date: value })}
                  renderInside
                />
              </Flex>
            </Flex>
          </Section>

          <Modal.Footer>
            <Button size="small" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button size="small" onClick={handleApply}>
              Apply
            </Button>
          </Modal.Footer>
        </Flex>
      </FormProvider>
    </Modal>
  );
};
